import buttonStyles from '../Button/Button.scss';
import styles from './OrderSummary.scss';

import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMobile } from 'src/hooks/UseResponsive';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { ContentType, InternalLink, LinkVariant } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Button } from 'src/components/Button/Button';
import { useCart } from 'src/components/Cart/UseCart';
import { CheckoutRoutePath } from 'src/components/Checkout/Checkout';
import { HardwarePayment } from 'src/components/OrderSummary/HardwarePayment/HardwarePayment';
import { FetchingPromoProvider } from 'src/components/OrderSummary/PromoSummary/FetchingPromoContext/FetchingPromoContext';
import { PromoSummary } from 'src/components/OrderSummary/PromoSummary/PromoSummary';
import { SummarySection } from 'src/components/OrderSummary/SummarySection/SummarySection';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { redesignV2RoutePath } from 'src/constants/redesignV2';
import { useDCS } from 'src/hooks/useDCS';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { IntercomWrapper } from 'src/services/intercom';
import { CONNECT_TO_SALES_PATH } from '../App/Routes/Routes';
import { InternalLinkV2 } from '../ContentLinkV2/InternalLinkV2';
import { DueToday } from './DueToday/DueToday';

interface OrderSummaryProps {
  isContinueButtonDisabled?: boolean;
  continueButtonClickHandler?: React.MouseEventHandler<HTMLButtonElement>;
  topOffset?: number;
  backButtonVisible?: boolean;
  isCheckout?: boolean;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  isContinueButtonDisabled,
  continueButtonClickHandler,
  topOffset,
  backButtonVisible = true,
  isCheckout,
}) => {
  const orderSummaryRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { cart, checkout } = useCart();
  const [userMustContactSales, setUserMustContactSales] = useState<boolean>(false);
  const { productsPricings, bundlesPricings } = useDCS({
    bundleIds:  [cart.bundle?.id],
    productIds: cart.products.map(p => p.id),
  });
  const location = useLocation();
  const isMobile = useMobile();
  const isReviewPath = location.pathname === (redesignV2RoutePath.PURCHASE + CheckoutRoutePath.REVIEW);

  useDeepCompareEffect(() => {
    const userMustContactSales =
      bundlesPricings.some(b => b && !b.isSelfServicePurchaseEnabled) ||
      productsPricings.some(p => p && !p.isSelfServicePurchaseEnabled);

    setUserMustContactSales(userMustContactSales);
  }, [cart, productsPricings, bundlesPricings]);

  useEffect(() => {
    if (topOffset === undefined) return;
    const adjustedOffset = topOffset - (wrapperRef.current?.offsetTop || 0);
    if (adjustedOffset > 0) {
      orderSummaryRef.current?.style.setProperty('--order-summary-offset', `${adjustedOffset}px`);
    }
  }, [topOffset]);

  useEffect(() => {
    if (isMobile) {
      IntercomWrapper.update({
        vertical_padding: 100,
      });
    }

    return () => {
      IntercomWrapper.update({
        vertical_padding: 20,
      });
    };
  }, [isMobile]);

  return (
    <section ref={orderSummaryRef} className={cx(styles.orderSummary, { [styles.orderSummaryCheckout] : isCheckout })}>
      <TextV2
        tagName="h2"
        variant="h4"
        className={styles.title}
        value={t('order-summary.title')}
      />
      <div className={styles.wrapper} ref={wrapperRef}>
        {cart.softwarePlan && (
          <SummarySection
            subheading={t('order-summary.software-plan', { name: cart.softwarePlan.name })}
            price={t('order-summary.monthly-price', { price: checkout.softwareTotal })}
            description={t('order-summary.fee-waived-30-days')}
            data-test="software-plan"
          />
        )}
        <HardwarePayment isCheckout={isCheckout} />
        <FetchingPromoProvider>
          {(!isCheckout || (isCheckout && checkout.promo.applied)) && <PromoSummary promoBoxVisible={!isCheckout} data-test="promo-summary" />}
        </FetchingPromoProvider>
        {isReviewPath && <DueToday />}
        <div className={cx(styles.action, { [styles.actionFixed] : isMobile })}>
          {userMustContactSales ? (
            <InternalLinkV2
              className={cx(buttonStyles.button, buttonStyles.buttonVariantPrimary)}
              entry={{
                contentType: ContentType.INTERNAL_LINK,
                page: { slug: CONNECT_TO_SALES_PATH },
                title: t('contact-sales'),
              } as FlatEntry<InternalLink>}
            />
          ) : (
            <Button
              variant={LinkVariant.PRIMARY}
              isFullWidth
              disabled={isContinueButtonDisabled}
              onClick={continueButtonClickHandler}
              data-test="continue-button"
            >
              {t('continue')}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

OrderSummary.displayName = 'OrderSummary';
